/**
 * Instagram Bridge Page
 * Handles OAuth callback for web and native: validates state, calls SSO API for
 * login/signup, and redirects to dashboard or signup step 4 (or returnPath for other contexts).
 *
 * @author Varun Pandey
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import { InstagramAuthCore } from '@/instagram/InstagramAuthCore';
import { AuthService, getAuthErrorMessage, handleSsoResult } from '@/services/authService';
import { ArtistService } from '@/services/artistService';
import { DeepLinkService } from '@/services/deepLinkService';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { Loader } from '@/components/ui/Loader';

/** SessionStorage key set by DeepLinkService when app was opened via Universal Link/App Link. */
export const INSTAGRAM_BRIDGE_DEEP_LINK_FLAG = 'instagramBridgeOpenedViaDeepLink';

function useCodeAndState(): { code: string | null; state: string | null } {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  // Optional: parse hash if Instagram ever returns params there
  if ((!code || !state) && typeof window !== 'undefined' && window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, '').replace('#_', ''));
    return {
      code: code ?? hashParams.get('code'),
      state: state ?? hashParams.get('state'),
    };
  }
  return { code, state };
}

export function InstagramBridgePage() {
  const navigate = useNavigate();
  const { code, state } = useCodeAndState();
  const fetchArtistDetails = useArtistDetailsStore((s) => s.fetchArtistDetails);
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    if (!code || !state) {
      toast.error('Invalid Instagram callback. Missing code or state.');
      navigate('/login', { replace: true, state: { message: 'Instagram sign-in was incomplete.' } });
      return;
    }

    const run = async () => {
      didRunRef.current = true;

      // Fallback path logging (native): if we weren't opened via deep link, log so teams know UL/AL need config
      if (Capacitor.isNativePlatform()) {
        const openedViaDeepLink = sessionStorage.getItem(INSTAGRAM_BRIDGE_DEEP_LINK_FLAG);
        sessionStorage.removeItem(INSTAGRAM_BRIDGE_DEEP_LINK_FLAG);
        if (!openedViaDeepLink) {
          console.warn('[InstagramBridge] FALLBACK PATH – Universal Links / App Links may not be configured.');
        }
      }

      try {
        const result = await InstagramAuthCore.consumeCallback(code, state);

        if (result.context === 'login' || result.context === 'signup') {
          const ssoResult = await AuthService.signupSso('instagram', result.code);
          await handleSsoResult(ssoResult, navigate, fetchArtistDetails);
          return;
        }

        // Connect/photos: call artist Instagram connect API, store token, then redirect
        if (result.context === 'connect' || result.context === 'photos') {
          try {
            await ArtistService.connectInstagramAndStore(result.code);
          } catch (err) {
            console.error('[InstagramBridge] Instagram connect failed:', err);
            toast.error(getAuthErrorMessage(err, 'Instagram connect failed. Please try again.'));
          }
          DeepLinkService.resetInstagramBridgeHandled();
          const safePath =
            result.returnPath && result.returnPath.startsWith('/') ? result.returnPath : '/user-home';
          navigate(safePath, { replace: true });
          return;
        }

        // Other non-SSO contexts: redirect to returnPath or default
        const safePath = result.returnPath && result.returnPath.startsWith('/') ? result.returnPath : '/user-home';
        navigate(safePath, { replace: true });
      } catch (err) {
        console.error('[InstagramBridge] Error:', err);
        toast.error(getAuthErrorMessage(err, 'Instagram sign-in failed. Please try again.'));
        navigate('/login', { replace: true, state: { message: 'Instagram sign-in failed.' } });
      }
    };

    void run();
  }, [code, state, navigate, fetchArtistDetails]);

  return <Loader size="fullscreen" message="Validating with Instagram..." />;
}
